import { useEffect, useState, type FormEvent } from 'react';
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import { useLocation, useNavigate } from '../../app/router';
import { Logo } from '../../components/Logo';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { ApiError, apiRequest } from '../../lib/api';
import { saveToken, tenantTokenKey } from '../../app/session';
import { useTranslation, type AppLocale } from '../../i18n/i18n';

interface BrandingResponse {
  company?: {
    displayName: string;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
  };
  status?: string;
}

interface LoginResponse {
  accessToken: string;
  user?: { locale?: AppLocale };
}

export function LoginPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { locale, setLocale, t } = useTranslation();
  const [branding, setBranding] = useState<BrandingResponse['company']>();
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [notice, setNotice] = useState(() =>
    new URLSearchParams(search).get('passwordChanged') === '1'
      ? 'Lozinka je promijenjena. Prijavite se novom lozinkom.'
      : ''
  );
  const [previewResetUrl, setPreviewResetUrl] = useState('');

  useEffect(() => {
    apiRequest<BrandingResponse>('/api/v1/tenant/branding')
      .then((response) => {
        setBranding(response.company);
        if (response.company) {
          document.documentElement.style.setProperty(
            '--brand',
            response.company.primaryColor
          );
          document.documentElement.style.setProperty(
            '--accent',
            response.company.secondaryColor
          );
          document.title = `${response.company.displayName} · Šihterica`;
        }
      })
      .catch(() => undefined);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await apiRequest<LoginResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ identity, password })
      });
      saveToken(tenantTokenKey, response.accessToken);
      if (response.user?.locale) setLocale(response.user.locale);
      const params = new URLSearchParams(search);
      const tenant = params.get('tenant');
      navigate(
        params.get('kiosk') === '1'
          ? `/kiosk/evidencija${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`
          : '/app',
        { replace: true }
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Prijava trenutačno nije dostupna.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function requestPasswordReset(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const response = await apiRequest<{
        message: string;
        previewResetUrl?: string;
      }>('/api/v1/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ identity })
      });
      setNotice(response.message);
      setPreviewResetUrl(response.previewResetUrl ?? '');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Zahtjev nije moguće poslati.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story">
        <div className="login-story__top">
          <Logo name={branding?.displayName ?? 'Šihterica'} logoUrl={branding?.logoUrl} />
        </div>
        <div className="login-story__content">
          <span className="eyebrow">{t('auth.tagline')}</span>
          <h1>{t('auth.hero')}</h1>
          <p>{t('auth.heroBody')}</p>
        </div>
        <div className="login-story__pulse">
          <span className="pulse-dot" />
          {t('auth.ready')}
        </div>
      </section>

      <section className="login-panel">
        <form className="login-card" onSubmit={forgotMode ? requestPasswordReset : submit}>
          <div className="login-card__brand-mobile">
            <Logo name={branding?.displayName ?? 'Šihterica'} logoUrl={branding?.logoUrl} />
          </div>
          <LanguageSwitcher
            value={locale}
            onChange={setLocale}
            ariaLabel={t('settings.language')}
            variant="login"
          />
          <span className="eyebrow">{forgotMode ? 'SIGURAN OPORAVAK RAČUNA' : t('auth.welcome')}</span>
          <h2>{forgotMode ? 'Postavite novu lozinku' : t('auth.login')}</h2>
          <p className="muted">{forgotMode ? 'Upišite e-mail ili korisničko ime. Poslat ćemo vam jednokratnu poveznicu.' : t('auth.continue')}</p>

          <label className="field">
            <span>{t('auth.identity')}</span>
            <span className="field__control">
              <Mail size={18} />
              <input
                autoComplete="username"
                value={identity}
                onChange={(event) => setIdentity(event.target.value)}
                placeholder="ime@firma.hr"
                required
              />
            </span>
          </label>

          {!forgotMode && <label className="field">
            <span>{t('auth.password')}</span>
            <span className="field__control">
              <LockKeyhole size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Vaša lozinka"
                minLength={8}
                required
              />
              <button
                className="icon-button"
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Sakrij lozinku' : 'Prikaži lozinku'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>}

          {notice && <div className="form-success" role="status">{notice}</div>}
          {error && <div className="form-error" role="alert">{error}</div>}

          <button className="button button--primary button--large" disabled={loading}>
            {loading ? '…' : forgotMode ? 'Pošalji poveznicu' : t('auth.submit')}
            {!loading && <ArrowRight size={18} />}
          </button>
          {previewResetUrl && (
            <button className="button button--ghost" type="button" onClick={() => navigate(previewResetUrl)}>
              Otvori testnu poveznicu
            </button>
          )}
          <button
            className="text-button"
            type="button"
            onClick={() => {
              setForgotMode((value) => !value);
              setError('');
              setNotice('');
              setPreviewResetUrl('');
            }}
          >
            {forgotMode ? 'Natrag na prijavu' : t('auth.forgot')}
          </button>
        </form>
      </section>
    </main>
  );
}
